import type { Ref } from 'vue'

import type { DraftRecordItem } from './useDraftDataManager'
import { MacroEditorWheelAction } from '../types'

interface RecordMutationsContext {
  workingData: Ref<DraftRecordItem[]>
  insertAt: (index: number, record: DraftRecordItem) => void
  updateAt: (index: number, patch: Partial<DraftRecordItem>) => boolean
  markAsDeleted: (index: number) => boolean
  findIndexByRef: (record: DraftRecordItem) => number
}

/**
 * 纯数据改写，不包含 UI/事件流程。
 */
export function useRecordMutations(context: RecordMutationsContext) {
  const { workingData, insertAt, updateAt, markAsDeleted, findIndexByRef } = context

  /**
   * 按 keystroke 匹配成对的 down/up。
   * 允许中间穿插其他按键动作（如 X↓ Y↓ X↑ Y↑），不因遇到其他按键的按下/抬起而中断。
   */
  const findKeyPairIndexes = (index: number) => {
    const current = workingData.value[index]
    if (current?.type !== 1) return null

    if (current.action === 1) {
      for (let cursor = index + 1; cursor < workingData.value.length; cursor++) {
        const item = workingData.value[cursor]
        if (!item || item.__status === 'deleted') continue
        if (item.type === 1 && item.keystroke === current.keystroke && item.action === 0) {
          return [index, cursor] as const
        }
      }
    }

    if (current.action === 0) {
      for (let cursor = index - 1; cursor >= 0; cursor--) {
        const item = workingData.value[cursor]
        if (!item || item.__status === 'deleted') continue
        if (item.type === 1 && item.keystroke === current.keystroke && item.action === 1) {
          return [cursor, index] as const
        }
      }
    }

    return null
  }

  const applyKeyEdit = (record: DraftRecordItem, keystroke: number) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 1) return false

    const pairIndexes = findKeyPairIndexes(index)
    if (pairIndexes) {
      for (const pairIndex of pairIndexes) {
        updateAt(pairIndex, { keystroke, __placeholder: false })
      }
      return true
    }

    return updateAt(index, { keystroke, __placeholder: false })
  }

  /**
   * 将按键成对动作合并为滚轮：保留当前编辑位置，删除配对的另一半。
   * 若当前是孤立按键（无配对），则就地转为滚轮。
   */
  const convertKeyToWheel = (
    record: DraftRecordItem,
    direction: Pick<MacroEditorWheelAction, 'hWheel' | 'vWheel'>,
  ) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 1) return false

    const pairIndexes = findKeyPairIndexes(index)
    if (pairIndexes) {
      const pairedIndex = pairIndexes[0] === index ? pairIndexes[1] : pairIndexes[0]
      markAsDeleted(pairedIndex)
    }

    const nextStatus =
      current.__status === 'added' || current.__status === 'deleted' ? current.__status : 'modified'

    workingData.value[index] = {
      type: 3,
      vWheel: direction.vWheel,
      hWheel: direction.hWheel,
      __id: current.__id,
      __status: nextStatus,
    }

    return true
  }

  /**
   * 将滚轮改为按键：只保留按下动作（action=1）。
   */
  const convertWheelToKey = (record: DraftRecordItem, keystroke: number) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 3) return false

    const nextStatus =
      current.__status === 'added' || current.__status === 'deleted' ? current.__status : 'modified'

    workingData.value[index] = {
      type: 1,
      keystroke,
      action: 1,
      __id: current.__id,
      __status: nextStatus,
    }

    return true
  }

  const addKeyPlaceholderPair = (anchorRecord: DraftRecordItem) => {
    const index = findIndexByRef(anchorRecord)
    if (index < 0) return false

    insertAt(index + 1, {
      type: 1,
      keystroke: 0,
      action: 1,
      __placeholder: true,
    })

    insertAt(index + 2, {
      type: 1,
      keystroke: 0,
      action: 0,
      __placeholder: true,
    })

    return true
  }

  const finalizeAddedKeyPair = (anchorRecord: DraftRecordItem, keystroke: number) => {
    const index = findIndexByRef(anchorRecord)
    if (index < 0) return false

    updateAt(index + 1, {
      type: 1,
      action: 1,
      keystroke,
      __placeholder: false,
    })

    updateAt(index + 2, {
      type: 1,
      action: 0,
      keystroke,
      __placeholder: false,
    })

    return true
  }

  const updateCoordinate = (
    record: DraftRecordItem,
    payload: { x_coordinate?: number; y_coordinate?: number },
  ) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 2) return false

    return updateAt(index, {
      x_coordinate: payload.x_coordinate ?? current.x_coordinate,
      y_coordinate: payload.y_coordinate ?? current.y_coordinate,
    })
  }

  const updateDelay = (record: DraftRecordItem, delay: number) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 4) return false

    return updateAt(index, {
      time: delay,
    })
  }

  const updateWheel = (
    record: DraftRecordItem,
    direction: Pick<MacroEditorWheelAction, 'hWheel' | 'vWheel'>,
  ) => {
    const index = findIndexByRef(record)
    if (index < 0) return false

    const current = workingData.value[index]
    if (current.type !== 3) return false

    return updateAt(index, direction)
  }

  return {
    applyKeyEdit,
    convertKeyToWheel,
    convertWheelToKey,
    addKeyPlaceholderPair,
    finalizeAddedKeyPair,
    updateCoordinate,
    updateDelay,
    updateWheel,
  }
}
